import { PartitionBar, type PartitionBarItem } from '@civilgrid/ui/components';
import { type ReactNode, useMemo } from 'react';
import { useSnapshot } from 'valtio';
import store from '../store';
import { getRequestProgressData, getResearchProgressData } from './progressUtils';

export const useRequestProgressData = (): PartitionBarItem[] => {
  const { agencies, connectbaseCoverageEnabled, dataRequests, isFullUtilitiesExperienceEnabled } = useSnapshot(
    store,
  ) as typeof store;

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  return useMemo<PartitionBarItem[]>(() => {
    const { completed, inProgress, requestable, cancelled } = getRequestProgressData({
      agencies,
      dataRequests,
      connectbaseCoverage: connectbaseCoverageEnabled,
    });

    return [
      {
        label: isFullUtilitiesExperienceEnabled ? 'Completed' : 'Available Digitally',
        value: completed,
        tone: 'green',
        description: 'Number of agencies with completed data requests',
      },
      {
        label: 'In Progress',
        value: inProgress,
        tone: 'blue',
        description: 'Number of agencies with data requests in progress',
      },
      {
        label: 'Available for Request',
        value: requestable,
        tone: 'yellow',
        description: 'Number of agencies available for data requests',
      },
      // included only when non-zero so the zero-width segment doesn't add a stray border to the bar
      ...(cancelled > 0
        ? [
            {
              label: 'Cancelled',
              value: cancelled,
              tone: 'grey' as const,
              hideFromLegend: true,
              description: 'Number of agencies with cancelled or dismissed data requests',
            },
          ]
        : []),
    ];
  }, [agencies, dataRequests, connectbaseCoverageEnabled]);
};

export const useResearchProgressData = (): PartitionBarItem[] => {
  const { agencies, connectbaseCoverageEnabled, dataRequests } = useSnapshot(store) as typeof store;

  return useMemo<PartitionBarItem[]>(() => {
    const { initiated, notStarted } = getResearchProgressData({
      agencies,
      dataRequests,
      connectbaseCoverage: connectbaseCoverageEnabled,
    });

    const sum = initiated + notStarted;

    const initiatedPercentage = Math.round((initiated / sum) * 100);
    const notStartedPercentage = Math.round((notStarted / sum) * 100);

    return [
      {
        label: 'Site Research Completed/Initiated',
        value: initiatedPercentage,
        displayValue: `${initiatedPercentage}%`,
        tone: 'green',
        description: 'Site research complete/initiated with digital data and requested maps',
      },
      {
        label: 'Site Research Available',
        value: notStartedPercentage,
        displayValue: `${notStartedPercentage}%`,
        tone: 'yellow',
        description: 'Request maps to increase coverage',
      },
    ];
  }, [agencies, dataRequests, connectbaseCoverageEnabled]);
};

export function ProgressCard({
  title,
  tooltip,
  items,
  'data-testid': dataTestId,
}: {
  title: ReactNode;
  tooltip: ReactNode;
  items: PartitionBarItem[];
  'data-testid'?: string;
}) {
  return (
    <div
      data-testid={dataTestId}
      className="col-span-2 h-fit space-y-2 rounded-lg border border-grey-30 bg-secondary-light/30 p-4"
    >
      <div>
        <div className="text-lg">{title}</div>
        <div className="text-xs text-secondary">{tooltip}</div>
      </div>
      <PartitionBar items={items} data-testid={dataTestId ? `${dataTestId}-bar` : undefined} />
    </div>
  );
}
